// Reader blocks of a content collection — `collection_search` and `comments`.
//
// ONE markup for every surface. Interactive surfaces (the Run view, live
// preview) drive it through React with a transport the host supplies via
// `ReaderApiContext`; the static site renders the same markup server-side and
// `collectionSearchScript` / `commentsScript` (scripts.ts) take it over, keyed
// on the `data-pg-*` attributes below. With neither (the designer canvas) the
// blocks show sample content so an author can see the design.

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { CollectionSearchBlock, CommentsBlock } from '@twasta/page-schema'

import { substituteVars } from './actions/substitute'

export interface SearchItem {
  t: string          // title
  u: string          // url
  s?: string         // summary
  d?: string         // display date
  p?: string         // ISO date
  i?: string         // image
  k?: string[]       // terms (tags, category, author…)
}

export interface CommentItem { name: string; body: string; date?: string }

export interface ReaderApi {
  searchIndex?: (collection: string) => Promise<SearchItem[]>
  listComments?: (collection: string, slug: string) => Promise<CommentItem[]>
  postComment?: (collection: string, slug: string, payload: {
    name: string; email: string; body: string; elapsed?: number; hp?: string
  }) => Promise<{ status: string }>
}

export const ReaderApiContext = createContext<ReaderApi | null>(null)

// ── search ────────────────────────────────────────────────────────────────

/** Every word must appear in the title, summary or terms; title hits rank
 *  first. The static-site script implements the SAME rule. */
export function matchSearch(items: SearchItem[], query: string, limit = 12): SearchItem[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return []
  const scored: { item: SearchItem; score: number }[] = []
  for (const it of items) {
    const title = (it.t || '').toLowerCase()
    const hay = `${title} ${(it.s || '').toLowerCase()} ${(it.k || []).join(' ').toLowerCase()}`
    if (!words.every((w) => hay.includes(w))) continue
    scored.push({ item: it, score: words.filter((w) => title.includes(w)).length })
  }
  scored.sort((a, b) => b.score - a.score)
  return scored.slice(0, limit).map((x) => x.item)
}

const SAMPLE_RESULTS: SearchItem[] = [
  { t: 'A matching record', u: '#', s: 'Results appear here as the visitor types.', d: 'Today' },
  { t: 'Another match', u: '#', s: 'Searches titles, summaries and tags.', d: 'Yesterday' },
]

export function CollectionSearchRenderer({ block, design = false }: {
  block: CollectionSearchBlock; design?: boolean
}) {
  const api = useContext(ReaderApiContext)
  const [q, setQ] = useState(() => {
    if (typeof window === 'undefined') return ''
    try { return new URLSearchParams(window.location.search).get('q') || '' } catch { return '' }
  })
  const [items, setItems] = useState<SearchItem[] | null>(null)
  const [failed, setFailed] = useState(false)
  const loading = useRef(false)
  const limit = block.limit || 12

  const load = () => {
    if (items || loading.current || design) return
    loading.current = true
    const viaApi = api?.searchIndex ? api.searchIndex(block.collection) : null
    const viaFile = !viaApi && block.indexUrl
      ? fetch(block.indexUrl).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      : null
    const p = viaApi || viaFile
    if (!p) { loading.current = false; return }
    p.then((list: SearchItem[]) => setItems(Array.isArray(list) ? list : []))
      .catch(() => setFailed(true))
      .finally(() => { loading.current = false })
  }
  useEffect(() => { if (q) load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const results = useMemo(
    () => (design && q ? SAMPLE_RESULTS : matchSearch(items || [], q, limit)),
    [items, q, limit, design],
  )
  const searched = q.trim().length > 0 && (items !== null || design)

  return (
    <div data-pg-search="" data-index={block.indexUrl || undefined} data-limit={String(limit)}
         style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 }}>
      <input
        type="search" data-pg-search-input="" value={q}
        placeholder={block.placeholder || 'Search…'}
        aria-label={block.placeholder || 'Search'}
        onFocus={load}
        onChange={(e) => { setQ(e.currentTarget.value); load() }}
        style={{
          padding: '11px 14px', fontSize: '0.95rem', width: '100%', boxSizing: 'border-box',
          border: '1px solid var(--pg-border, #e2e8f0)', borderRadius: 10,
          background: 'var(--pg-surface, #fff)', color: 'var(--pg-text, inherit)',
        }}
      />
      <ul data-pg-search-results="" aria-live="polite"
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {results.map((r) => (
          <li key={r.u + r.t} style={{ borderBottom: '1px solid var(--pg-border, #e2e8f0)', paddingBottom: 10 }}>
            <a href={r.u} style={{ fontWeight: 700, color: 'var(--pg-link, var(--pg-primary))', textDecoration: 'none' }}>{r.t}</a>
            {r.d && <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--pg-text-secondary, #64748b)' }}>{r.d}</span>}
            {r.s && <p style={{ margin: '4px 0 0', fontSize: '0.88rem', color: 'var(--pg-text-secondary, #475569)' }}>{r.s}</p>}
          </li>
        ))}
      </ul>
      <p data-pg-search-empty="" hidden={!(searched && results.length === 0)}
         style={{ margin: 0, fontSize: '0.88rem', color: 'var(--pg-text-secondary, #64748b)' }}>
        {failed ? 'Search is not available right now.' : (block.emptyText || 'Nothing matches that search.')}
      </p>
    </div>
  )
}

// ── comments ──────────────────────────────────────────────────────────────

const SAMPLE_COMMENTS: CommentItem[] = [
  { name: 'A reader', body: 'Approved comments appear here, newest last.', date: 'Today' },
]

const INPUT: React.CSSProperties = {
  padding: '9px 12px', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box',
  border: '1px solid var(--pg-border, #e2e8f0)', borderRadius: 8,
  background: 'var(--pg-surface, #fff)', color: 'var(--pg-text, inherit)', fontFamily: 'inherit',
}
const HONEYPOT: React.CSSProperties = {
  position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden',
}

export function CommentsRenderer({ block, vars, endpoint, design = false }: {
  block: CommentsBlock
  vars?: Record<string, unknown>
  /** The static site's absolute comment URL (the script reads it). */
  endpoint?: string
  design?: boolean
}) {
  const api = useContext(ReaderApiContext)
  const slug = substituteVars(block.recordSlug || '', vars || {}).trim()
  const [comments, setComments] = useState<CommentItem[]>(design ? SAMPLE_COMMENTS : [])
  const [status, setStatus] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const started = useRef(Date.now())
  const live = !design && !!api?.listComments && !!slug && !slug.includes('{{')

  useEffect(() => {
    if (!live) return
    api!.listComments!(block.collection, slug).then(setComments).catch(() => setComments([]))
  }, [live, block.collection, slug]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (!api?.postComment || !live) {
      if (endpoint) return                  // static site: the script handles it
      e.preventDefault()                    // nowhere to send it on this surface
      if (!design) setStatus({ tone: 'err', text: 'Comments cannot be posted here.' })
      return
    }
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    setBusy(true)
    setStatus(null)
    try {
      const res = await api.postComment(block.collection, slug, {
        name: String(f.get('name') || ''), email: String(f.get('email') || ''),
        body: String(f.get('body') || ''), hp: String(f.get('_pg_hp') || ''),
        elapsed: (Date.now() - started.current) / 1000,
      })
      e.currentTarget?.reset?.()
      if (res.status === 'approved') {
        setComments(await api.listComments!(block.collection, slug))
        setStatus({ tone: 'ok', text: 'Thanks — your comment is published.' })
      } else {
        setStatus({ tone: 'ok', text: 'Thanks — your comment will appear once it is approved.' })
      }
    } catch (err) {
      setStatus({ tone: 'err', text: err instanceof Error ? err.message : 'Could not post your comment.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section data-pg-comments="" data-endpoint={endpoint || undefined}
             style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 24 }}>
      <h2 style={{ margin: 0, fontSize: '1.3rem' }}>
        {block.heading || 'Comments'}{' '}
        <span data-pg-comments-count="" style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--pg-text-secondary, #64748b)' }}>
          {comments.length ? `(${comments.length})` : ''}
        </span>
      </h2>
      <ol data-pg-comments-list="" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {comments.map((c, i) => (
          <li key={i} style={{ padding: '12px 14px', border: '1px solid var(--pg-border, #e2e8f0)', borderRadius: 10 }}>
            <strong>{c.name}</strong>
            {c.date && <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--pg-text-secondary, #64748b)' }}>{c.date}</span>}
            <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{c.body}</p>
          </li>
        ))}
      </ol>
      <p data-pg-comments-empty="" hidden={comments.length > 0}
         style={{ margin: 0, color: 'var(--pg-text-secondary, #64748b)' }}>
        {block.emptyText || 'No comments yet.'}
      </p>
      <form data-pg-comments-form="" onSubmit={onSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16,
                     border: '1px solid var(--pg-border, #e2e8f0)', borderRadius: 12, position: 'relative' }}>
        <strong>{block.formHeading || 'Leave a comment'}</strong>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          <input name="name" required maxLength={80} placeholder="Your name" aria-label="Your name" style={INPUT} />
          <input name="email" type="email" maxLength={160} placeholder="Email (not published)" aria-label="Email (not published)" style={INPUT} />
        </div>
        <textarea name="body" required maxLength={4000} rows={4} placeholder="Your comment" aria-label="Your comment" style={INPUT} />
        <div aria-hidden="true" style={HONEYPOT}>
          <input name="_pg_hp" tabIndex={-1} autoComplete="off" />
        </div>
        <button type="submit" disabled={busy} style={{
          alignSelf: 'flex-start', padding: '10px 18px', fontWeight: 600, border: 'none', borderRadius: 8,
          background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', cursor: busy ? 'wait' : 'pointer',
        }}>{block.submitLabel || 'Post comment'}</button>
        <p data-pg-comments-status="" role="status" hidden={!status}
           style={{ margin: 0, fontSize: '0.88rem',
                    color: status?.tone === 'err' ? 'var(--pg-error, #dc2626)' : 'var(--pg-success, #16a34a)' }}>
          {status?.text}
        </p>
      </form>
    </section>
  )
}
